"use client"

import { useNavigate } from "react-router-dom"
import { Package, PackageX } from "lucide-react"
import Pagination from "../../Common/Pagination"

function SkeletonRows({ columnCount }) {
  return Array.from({ length: 8 }).map((_, i) => (
    <tr key={i} className="animate-pulse border-b border-gray-100">
      <td className="px-4 py-4">
        <div className="h-4 bg-gray-200 rounded w-36" />
      </td>
      {Array.from({ length: columnCount - 1 }).map((_, j) => (
        <td key={j} className="px-4 py-4">
          <div className="h-4 bg-gray-200 rounded w-16" />
        </td>
      ))}
    </tr>
  ))
}

const getMovementBadgeClass = (level) => {
  switch (level) {
    case "High":
      return "bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium"
    case "Medium":
      return "bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-medium"
    case "Low":
      return "bg-slate-100 text-slate-700 px-2 py-1 rounded-full text-xs font-medium"
    case "Dead":
      return "bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium"
    default:
      return "bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium"
  }
}

const getP0StatusBadgeClass = (status) => {
  switch (status) {
    case "Out of Stock":
      return "bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium"
    case "Running Out Soon":
      return "bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-medium"
    case "Reorder Required":
      return "bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-medium"
    case "Non-Moving":
      return "bg-slate-100 text-slate-700 px-2 py-1 rounded-full text-xs font-medium"
    case "Healthy":
      return "bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium"
    default:
      return "bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium"
  }
}

const formatDaysLeft = (daysLeft) => {
  if (daysLeft === null || daysLeft === undefined) return "—"
  return Math.floor(daysLeft)
}

const EMPTY_MESSAGES = {
  "high-running": "No high-running items",
  "stock-out-risk": "No stock-out risk items",
  "non-moving": "No non-moving items",
  all: "No inventory items found",
}

export default function InventoryAnalyticsTable({
  activeTab,
  items,
  loading,
  searchTerm,
  isColorEnabled,
  isSubCategoryEnabled,
  daysFilter,
  currentPage,
  totalCount,
  itemsPerPage,
  setCurrentPage,
  setItemsPerPage,
}) {
  const navigate = useNavigate()

  const soldField = `issued_${daysFilter}`
  const periodDays = parseInt(daysFilter, 10) || 30
  const columnCount = 9 + (isSubCategoryEnabled ? 1 : 0) + (isColorEnabled ? 1 : 0)

  const handleRowClick = (item) => {
    navigate(`/dashboard/inventory/${item.inventory_id}`)
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-y border-gray-200">
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Item Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">SKU</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Category</th>
              {isSubCategoryEnabled && (
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Sub Category</th>
              )}
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Brand</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Size</th>
              {isColorEnabled && (
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Color</th>
              )}
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Available</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Sold</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Days Left</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Velocity</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading && items.length === 0 ? (
              <SkeletonRows columnCount={columnCount} />
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={columnCount} className="p-0">
                  <div className="flex flex-col items-center justify-center py-12 px-4">
                    <PackageX className="h-12 w-12 text-gray-300 mb-3" />
                    <p className="text-gray-500 font-medium">
                      {searchTerm ? "No items found matching your search" : EMPTY_MESSAGES[activeTab] || EMPTY_MESSAGES.all}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr
                  key={item.inventory_id}
                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => handleRowClick(item)}
                >
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <Package className="h-4 w-4 text-gray-400" />
                      </div>
                      <div className="font-medium">{item.item_name}</div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-500 font-mono">{item.inventory_id}</td>
                  <td className="px-4 py-4 text-sm text-gray-700">{item.category || "—"}</td>
                  {isSubCategoryEnabled && (
                    <td className="px-4 py-4 text-sm text-gray-700">{item.sub_category || "—"}</td>
                  )}
                  <td className="px-4 py-4 text-sm text-gray-700">{item.brand || "—"}</td>
                  <td className="px-4 py-4 text-sm text-gray-700">{item.size || "—"}</td>
                  {isColorEnabled && (
                    <td className="px-4 py-4 text-sm text-gray-700">{item.custom_attributes?.color || "—"}</td>
                  )}
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">
                    {item.available_qty} {item.unit_of_measurement || ""}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">
                    <span>{item[soldField] || 0}</span>
                    <span className="text-xs text-gray-400 ml-1">{((item[soldField] || 0) / periodDays).toFixed(1)} / day</span>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-700">{formatDaysLeft(item.days_left)}</td>
                  <td className="px-4 py-4">
                    <span className={`${getMovementBadgeClass(item.movement_level)} whitespace-nowrap`}>{item.movement_level}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span
                      className={`${getP0StatusBadgeClass(item.p0_status)} whitespace-nowrap`}
                      title={`Days left: ${formatDaysLeft(item.days_left)}`}
                    >
                      {item.p0_status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={totalCount}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={setItemsPerPage}
        pageSizeOptions={[10, 25, 50, 100]}
        itemLabel="items"
      />
    </>
  )
}
